import PropTypes from 'prop-types';

import Container, { ContainerProps as ContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '@classity-edtech/ui-base';
import { useConfig } from '../../../contexts';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { Link } from 'react-router-dom';
import { EarningsCalculator, PricingData } from '../../EarningsCalculator';

export interface HeroProps extends ContainerProps {
  title: string;
  isSmallerHeader: boolean;
  description: string;
  heroImage?: string;
  primaryCta?: string;
  hideCta?: boolean;
  showEarningsCalculator?: boolean;
  earningsPricing?: PricingData;
  earningsPlanId?: string;
}

export const Hero = ({ className, ...props }: HeroProps) => {
  const screens = useAppMediaQuery();

  const { learnUi } = useConfig() as any;
  const learnUiUrl = learnUi?.url;

  return (
    <Container className={className}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column-reverse', md: 'row' },
          alignItems: 'center',
          gap: 3,
          mt: { xs: 4, md: 9 },
          justifyContent: { xs: 'space-between', md: 'space-between' },
        }}
      >
        <Stack sx={{ gap: 1.5 }}>
          <Typography
            variant={props?.isSmallerHeader ? 'subtitle1' : screens.isDesktop ? 'h3' : 'h4'}
            variantMapping={{ h3: 'h1', h4: 'h1', subtitle1: 'h1' }}
            sx={{
              maxWidth: '450px',
              width: '100%',
              wordBreak: 'break-word',
              lineHeight: { xs: 'normal', md: 'normal' },
              letterSpacing: { xs: 0, md: '-2.5px' },
              fontFamily: screens.isMobile
                ? 'Gill Sans, sans-serif'
                : 'Gill Sans Infant Std, serif',
              fontSize: { xs: '36px', md: '64px' },
              fontWeight: screens.isMobile ? 400 : 500,
            }}
          >
            {props?.title}
          </Typography>
          <Typography
            variant={screens.isDesktop ? 'body1' : 'body2'}
            variantMapping={{ body1: 'h2', body2: 'h2' }}
            sx={{ maxWidth: '450px' }}
          >
            {props?.description}
          </Typography>
          {!props?.hideCta && (
            <Link
              to={`${learnUiUrl}/teach/signup`}
              style={{ textDecoration: 'none', width: 'fit-content' }}
            >
              <Button size={'large'} disableBottomFixed>
                <Typography variant='body2SemiBold'>{props?.primaryCta}</Typography>
              </Button>
            </Link>
          )}
        </Stack>
        {props?.showEarningsCalculator && props?.earningsPricing ? (
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', md: '480px' },
            }}
          >
            <EarningsCalculator pricing={props.earningsPricing} planId={props.earningsPlanId} />
          </Box>
        ) : (
          <Box
            component='img'
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', md: '680px' },
              height: 'auto',
              maxHeight: { xs: '42dvh', md: '50dvh' },
              objectFit: 'contain',
              objectPosition: 'top center',
            }}
            alt=''
            src={props?.heroImage}
          />
        )}
      </Box>
    </Container>
  );
};

Hero.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  isSmallerHeader: PropTypes.bool,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  heroImage: PropTypes.string,
  primaryCta: PropTypes.string,
  hideCta: PropTypes.bool,
  showEarningsCalculator: PropTypes.bool,
  earningsPricing: PropTypes.object,
  earningsPlanId: PropTypes.string,
};

Hero.defaultProps = {
  children: null,
  className: null,
  isSmallerHeader: false,
  hideCta: false,
  showEarningsCalculator: false,
  earningsPricing: null,
  earningsPlanId: null,
};

export default styled<any>(Hero)(
  ({ theme }) => `
  &.MuiBox-root {
  }
`,
);
